import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/ui/toast';
import { ProductAnalysisEntity } from '@/entities/product-analysis.entity';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useMarketplaceApi } from '@/context/marketplace.context';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

type UseCombinedAnalysisOrchestrationParams = {
    activeTable: ActionButtonsActiveTable;
    pickedProducts: string[];
    pickedBrandGroups: string[];
    accessToken: string;
    resolveSelectedBrandProductGroupIds: (selectedBrandGroupIds: string[]) => string[];
};

type UseCombinedAnalysisOrchestrationReturn = {
    openedCombinedAnalysis: ProductAnalysisEntity | null;
    isCombinedAnalysisModalOpen: boolean;
    isCombinedAnalysisLoading: boolean;
    combinedAnalysisError: string | null;
    closeCombinedAnalysis: () => void;
    openCombinedAnalysis: () => void;
};

const combinedAnalysisLogger = createClientLogger({
    scope: 'combined-analysis',
});

export const useCombinedAnalysisOrchestration = ({
    activeTable,
    pickedProducts,
    pickedBrandGroups,
    accessToken,
    resolveSelectedBrandProductGroupIds,
}: UseCombinedAnalysisOrchestrationParams): UseCombinedAnalysisOrchestrationReturn => {
    const { productsApi } = useMarketplaceApi();

    const [openedCombinedAnalysis, setOpenedCombinedAnalysis] = useState<ProductAnalysisEntity | null>(null);
    const [isCombinedAnalysisModalOpen, setIsCombinedAnalysisModalOpen] = useState<boolean>(false);
    const [isCombinedAnalysisLoading, setIsCombinedAnalysisLoading] = useState<boolean>(false);
    const [combinedAnalysisError, setCombinedAnalysisError] = useState<string | null>(null);
    const combinedAnalysisAbortController = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            if (combinedAnalysisAbortController.current) {
                combinedAnalysisAbortController.current.abort();
                combinedAnalysisAbortController.current = null;
            }
        };
    }, []);

    const closeCombinedAnalysis = useCallback(() => {
        if (combinedAnalysisAbortController.current) {
            combinedAnalysisAbortController.current.abort();
            combinedAnalysisAbortController.current = null;
        }

        setIsCombinedAnalysisModalOpen(false);
        setIsCombinedAnalysisLoading(false);
        setOpenedCombinedAnalysis(null);
        setCombinedAnalysisError(null);
    }, []);

    const openCombinedAnalysis = useCallback(() => {
        const uniqueProductIds =
            activeTable === 'brands'
                ? resolveSelectedBrandProductGroupIds([...new Set(pickedBrandGroups)])
                : [...new Set(pickedProducts)];

        if (!uniqueProductIds.length) {
            toast.warning(activeTable === 'brands' ? 'Выберите бренды' : 'Выберите продукты');
            return;
        }

        if (combinedAnalysisAbortController.current) {
            combinedAnalysisAbortController.current.abort();
            combinedAnalysisAbortController.current = null;
        }

        const abortController = new AbortController();
        combinedAnalysisAbortController.current = abortController;

        setIsCombinedAnalysisModalOpen(true);
        setIsCombinedAnalysisLoading(true);
        setCombinedAnalysisError(null);
        setOpenedCombinedAnalysis(null);

        void productsApi
            .getCombinedProductAnalysis(uniqueProductIds, {
                authToken: accessToken,
                signal: abortController.signal,
            })
            .then((response) => {
                setOpenedCombinedAnalysis(response.data);
            })
            .catch((error) => {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: combinedAnalysisLogger,
                    message: '[client.product-analysis.combined-load] Failed to load combined product analysis.',
                    error,
                    context: {
                        activeTable,
                        requestedProducts: uniqueProductIds.length,
                    },
                });
                setCombinedAnalysisError('Не удалось загрузить анализ выбранных продуктов.');
            })
            .finally(() => {
                if (combinedAnalysisAbortController.current === abortController) {
                    combinedAnalysisAbortController.current = null;
                    setIsCombinedAnalysisLoading(false);
                }
            });
    }, [accessToken, activeTable, pickedBrandGroups, pickedProducts, resolveSelectedBrandProductGroupIds]);

    return {
        openedCombinedAnalysis,
        isCombinedAnalysisModalOpen,
        isCombinedAnalysisLoading,
        combinedAnalysisError,
        closeCombinedAnalysis,
        openCombinedAnalysis,
    };
};
